import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormControl, FormHelperText, Grid, TextField } from '@mui/material';

import { BACKGROUND_FIELD_DESCRIPTIONS } from '#apps/client-web/features/audit-form-background/constants';
import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { useFormDescriptions } from '#apps/client-web/features/settings/hooks/use-form-descriptions';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';

export type FloorsFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const FloorsFormDialog: FC<PropsWithChildren<FloorsFormDialogProps>> = ({
  open,
  control,
  onClose,
}) => {
  const getDescription = useFormDescriptions(BACKGROUND_FIELD_DESCRIPTIONS);

  return (
    <SubFormDialog title="Floors" open={open} onClose={onClose}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Controller
            control={control}
            name="general.grossFloorArea"
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="Gross floor area (m²)"
                  type="number"
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            control={control}
            name="general.floors"
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="Above ground stories"
                  type="number"
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            control={control}
            name="general.parkadeLevels"
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="Parkade levels"
                  type="number"
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            control={control}
            name="general.otherFloors"
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="Other floors"
                  type="number"
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
    </SubFormDialog>
  );
};
